import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Image,
  ActivityIndicator,
  Dimensions,
  Modal,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import { AntDesign, MaterialCommunityIcons, Feather } from "@expo/vector-icons";
import * as ImagePicker from "expo-image-picker";
import {
  createIssueOrder,
  retrieveIssueSummary,
  shareIssueToOperation,
} from "../../../redux/dispatcher/portalDispatcher";
import teamsConfig from "../../../utils/constants/teams";
import rolesConfig from "../../../utils/constants/roles";
import { useToast } from "react-native-toast-notifications";
import { pushFileToS3 } from "../../../services/s3BucketService";

// ─── Constants ────────────────────────────────────────────────────────────────

const ISSUE_TYPE_LABELS: Record<string, string> = {
  CANCEL_ORDER: "Cancel Order",
  DAMAGED_ITEM: "Damaged Item",
  FAULTY_ITEM: "Faulty Item",
  MISSING_ITEM: "Missing Item",
  OTHER: "Other",
  WRONG_ITEM: "Wrong Item",
};

const ISSUE_DETAIL_LABELS: Record<string, string> = {
  DAMAGED_CARTONS: "Damaged cartons",
  DAMAGED_LABELS: "Damaged labels",
  DAMAGED_SOAKER_PADS: "Damaged soaker pads",
  FAULTY_CARTONS: "Faulty cartons",
  FAULTY_SOAKER_PADS: "Faulty soaker pads",
  MISSING_CARTONS: "Missing cartons",
  MISSING_LABEL: "Missing label",
  MISSING_PALLET: "Missing pallet",
  MISSING_SOAKER_PADS: "Missing soaker pads",
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const FieldRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | number;
}) => (
  <View style={styles.fieldRow}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <Text style={styles.fieldValue}>{value || "-"}</Text>
  </View>
);

const Avatar = ({ email }: { email?: string }) => (
  <View style={styles.avatar}>
    <Text style={styles.avatarText}>
      {(email || "U").charAt(0).toUpperCase()}
    </Text>
  </View>
);

const MessageThread = ({
  issue,
  depth = 0,
}: {
  issue: any;
  depth?: number;
}) => {
  if (!issue) return null;

  return (
    <View style={[styles.messageThread, { marginLeft: depth * 16 }]}>
      <View style={styles.messageHeader}>
        <Avatar email={issue.createdBy?.auth?.email} />
        <View style={styles.messageHeaderText}>
          <Text style={styles.messageSender}>
            {issue.createdBy?.auth?.email || "Unknown"}
          </Text>
          <Text style={styles.messageTime}>
            {new Date(issue.createdAt).toLocaleString()}
          </Text>
        </View>
      </View>

      <Text style={styles.messageBody}>{issue.message}</Text>

      {/* Attachments */}
      {issue.attachmentsSigned?.length > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.attachmentsRow}
        >
          {issue.attachmentsSigned.map((url: string, idx: number) => (
            <Image
              key={idx}
              source={{ uri: url }}
              style={styles.attachmentThumb}
              resizeMode="cover"
            />
          ))}
        </ScrollView>
      )}

      {/* Recursive children */}
      {issue.children?.map((child: any, index: number) => (
        <View key={child._id}>
          <View style={styles.threadDivider} />
          <MessageThread issue={child} depth={depth + 1} />
        </View>
      ))}
    </View>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────

export default function CustomerReportTicketScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useDispatch<any>();
  const toast = useToast();

  const { orderId: id } = route.params as { orderId: string };

  const {
    order: issue,
    loadingAction,
    loading,
  } = useSelector((state: any) => state.portal.issues);

  const { userInfo, currentRole } = useSelector(
    (state: any) => state.configurations,
  );

  const userTeam = userInfo?.team?.toUpperCase();
  const isAdminOrOwner = [
    rolesConfig.roles.admin,
    rolesConfig.roles.owner,
  ].includes(currentRole?.toUpperCase());
  const canShare =
    [
      teamsConfig.teams.operation,
      teamsConfig.teams.sales,
      teamsConfig.teams.admin,
    ].includes(userTeam) || isAdminOrOwner;

  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyAttachments, setReplyAttachments] = useState<
    {
      uri: string;
      fileName: string;
      uploading: boolean;
      uploaded: boolean;
      error?: string;
    }[]
  >([]);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const screenWidth = Dimensions.get("window").width;
  const isTablet = screenWidth >= 768;

  const isClosed = issue?.status === "CLOSE";

  useEffect(() => {
    if (id) dispatch(retrieveIssueSummary(id));
  }, [id]);

  const issueTypeLabel = issue?.issueType
    ? (ISSUE_TYPE_LABELS[issue.issueType] ?? issue.issueType)
    : undefined;

  const issueDetailLabel = issue?.otherIssueType
    ? issue?.issueType === "OTHER"
      ? issue.otherIssueType
      : (ISSUE_DETAIL_LABELS[issue.otherIssueType] ?? issue.otherIssueType)
    : undefined;

  const growerName =
    issue?.orderId?.subOrganization?.name ||
    issue?.createdBy?.subOrganization?.[0]?.name ||
    issue?.orderId?.organization?.name;

  // ── File pick ──
  // Replace the existing handlePickAttachment function
  const handlePickAttachment = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      allowsMultipleSelection: true,
      quality: 0.8,
    });

    if (!result.canceled) {
      const newAttachments = result.assets.map((asset) => ({
        uri: asset.uri,
        fileName: asset.fileName ?? `attachment_${Date.now()}.jpg`,
        uploading: false,
        uploaded: false,
      }));

      setReplyAttachments((prev) => [...prev, ...newAttachments]);

      // Upload each file to S3
      for (let i = 0; i < result.assets.length; i++) {
        const asset = result.assets[i];
        const attachmentIndex = replyAttachments.length + i;

        // Check file size — 5MB limit
        if (asset.fileSize && asset.fileSize > 5 * 1024 * 1024) {
          toast.show(`${asset.fileName || "File"} exceeds the 5MB size limit`, {
            type: "danger",
          });
          // Remove the placeholder that was added
          setReplyAttachments((prev) =>
            prev.filter((_, idx) => idx !== attachmentIndex),
          );
          continue;
        }

        setReplyAttachments((prev) =>
          prev.map((att, idx) =>
            idx === attachmentIndex ? { ...att, uploading: true } : att,
          ),
        );

        try {
          const timestamp = Date.now();
          const randomId = Math.random().toString(36).substring(2, 9);
          const extension = asset.fileName?.split(".").pop() || "jpg";
          const fileName = `reply_${timestamp}-${randomId}.${extension}`;

          // Convert URI to base64
          const base64 = await uriToBase64(asset.uri);
          await pushFileToS3(
            "POST",
            base64,
            fileName,
            asset.mimeType || "image/jpeg",
            "issues",
          );

          setReplyAttachments((prev) =>
            prev.map((att, idx) =>
              idx === attachmentIndex
                ? { ...att, uploading: false, uploaded: true, fileName }
                : att,
            ),
          );
        } catch (error) {
          console.error("Upload failed:", error);
          setReplyAttachments((prev) =>
            prev.map((att, idx) =>
              idx === attachmentIndex
                ? {
                    ...att,
                    uploading: false,
                    uploaded: false,
                    error: "Upload failed",
                  }
                : att,
            ),
          );
        }
      }
    }
  };

  // Add this helper below handlePickAttachment
  const uriToBase64 = async (uri: string): Promise<string> => {
    const response = await fetch(uri);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve((reader.result as string).split(",")[1]);
      reader.onerror = (err) => reject(err);
    });
  };

  const removeAttachment = (index: number) => {
    setReplyAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Submit reply ──
  const submitReply = () => {
    if (isClosed || !replyText.trim()) return;

    const payload = {
      orderId: issue?.orderId?._id,
      message: replyText.trim(),
      issueType: issue?.issueType,
      status: "OPEN",
      createdBy: userInfo._id,
      attachments: replyAttachments
        .filter((a) => a.uploaded)
        .map((a) => a.fileName),
      issueRef: issue?._id,
      createdByModel: "GO_USER",
    };

    dispatch(createIssueOrder(payload))
      .unwrap()
      .then(() => {
        dispatch(retrieveIssueSummary(id));
        setReplyText("");
        setReplyAttachments([]);
        setReplyOpen(false);
      })
      .catch((err: any) => {
        toast.show(err?.message || "Failed to submit reply", {
          type: "danger",
        });
      });
  };

  // ── Share ──
  const handleShare = () => {
    dispatch(
      shareIssueToOperation({
        payload: {
          issueId: issue?._id,
          email: "orders@multisteps.com.au",
        },
      }),
    )
      .unwrap()
      .then(() => {
        toast.show("Shared successfully with operation", {
          type: "success",
        });
      })
      .catch(() => {
        toast.show("Error sending to operation", {
          type: "danger",
        });
      });
  };

  // ─── Summary panel (modal on mobile, inline on tablet) ────────────────────
  const summaryContent = (
    <View style={styles.summaryPanel}>
      <View style={styles.summaryTitleRow}>
        <Text style={styles.summaryTitle}>Ticket Summary</Text>
        {!isTablet && (
          <TouchableOpacity onPress={() => setSummaryOpen(false)}>
            <AntDesign name="close" size={20} color="#374151" />
          </TouchableOpacity>
        )}
      </View>

      <FieldRow label="Status" value={issue?.status} />
      <FieldRow label="Ticket No" value={issue?.ticketNo} />
      <FieldRow label="Order No" value={issue?.orderId?.orderNo} />
      <FieldRow label="Issue Type" value={issueTypeLabel} />
      {issueDetailLabel && (
        <FieldRow label="Issue Detail" value={issueDetailLabel} />
      )}
      <FieldRow label="Grower" value={growerName} />
      <FieldRow label="Request By" value={issue?.createdBy?.auth?.email} />
      <FieldRow
        label="Created"
        value={
          issue?.createdAt ? new Date(issue.createdAt).toLocaleString() : "-"
        }
      />

      {canShare && (
        <TouchableOpacity style={styles.shareBtn} onPress={handleShare}>
          <Feather name="send" size={14} color="#fff" />
          <Text style={styles.shareBtnText}>Share to Operation</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
    >
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => {
            navigation.navigate("Root", {
              screen: "Home",
              params: {
                screen: "Customer",
                params: {
                  screen: "CustomerIssueReport",
                  params: { screen: "CustomerIssueScreen" },
                },
              },
            });
          }}
          style={styles.backBtn}
        >
          <AntDesign name="arrowleft" size={22} color="#111827" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Reported Issue</Text>
        {/* Info button (mobile only) */}
        {!isTablet && (
          <TouchableOpacity
            style={styles.infoBtn}
            onPress={() => setSummaryOpen(true)}
          >
            <Feather name="info" size={20} color="#374151" />
          </TouchableOpacity>
        )}
      </View>

      {loadingAction || loading || !issue ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color="#61B8FF" />
        </View>
      ) : (
        <View style={[styles.body, isTablet && styles.bodyTablet]}>
          {/* Left summary — tablet inline */}
          {isTablet && (
            <View style={styles.summaryColumn}>
              <ScrollView showsVerticalScrollIndicator={false}>
                {summaryContent}
              </ScrollView>
            </View>
          )}

          {isTablet && <View style={styles.verticalDivider} />}

          {/* Right: message thread + reply */}
          <View style={styles.threadColumn}>
            <ScrollView
              style={styles.threadScroll}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ paddingBottom: 24 }}
            >
              {issue ? (
                <MessageThread issue={issue} />
              ) : (
                <Text style={styles.emptyText}>No issue found.</Text>
              )}
            </ScrollView>

            {/* Reply box */}
            {replyOpen && !isClosed && (
              <View style={styles.replyBox}>
                <TextInput
                  style={styles.replyInput}
                  value={replyText}
                  onChangeText={setReplyText}
                  placeholder="Type your message..."
                  placeholderTextColor="#9CA3AF"
                  multiline
                  numberOfLines={4}
                  textAlignVertical="top"
                />

                {/* Attachment previews */}
                {replyAttachments.length > 0 && (
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    style={styles.attachPreviewRow}
                  >
                    {replyAttachments.map((att, idx) => (
                      <View key={idx} style={styles.attachPreviewItem}>
                        <Image
                          source={{ uri: att.uri }}
                          style={styles.attachPreviewThumb}
                        />
                        <TouchableOpacity
                          style={styles.attachRemoveBtn}
                          onPress={() => removeAttachment(idx)}
                        >
                          <AntDesign
                            name="closecircle"
                            size={16}
                            color="#EF4444"
                          />
                        </TouchableOpacity>
                        {att.uploading && (
                          <ActivityIndicator
                            size="small"
                            color="#61B8FF"
                            style={styles.attachSpinner}
                          />
                        )}
                      </View>
                    ))}
                  </ScrollView>
                )}

                <View style={styles.replyActions}>
                  <TouchableOpacity
                    style={styles.attachBtn}
                    onPress={handlePickAttachment}
                  >
                    <Feather name="paperclip" size={14} color="#fff" />
                    <Text style={styles.attachBtnText}>Attach</Text>
                  </TouchableOpacity>

                  <View style={styles.replyActionRight}>
                    <TouchableOpacity
                      style={styles.cancelBtn}
                      onPress={() => {
                        setReplyOpen(false);
                        setReplyText("");
                        setReplyAttachments([]);
                      }}
                    >
                      <Text style={styles.cancelBtnText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.submitBtn,
                        !replyText.trim() && styles.submitBtnDisabled,
                      ]}
                      onPress={submitReply}
                      disabled={!replyText.trim()}
                    >
                      <Text style={styles.submitBtnText}>Submit</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            )}

            {/* Reply button */}
            {issue?.status === "OPEN" && !replyOpen && (
              <View style={styles.replyBtnRow}>
                <TouchableOpacity
                  style={styles.replyBtn}
                  onPress={() => setReplyOpen(true)}
                >
                  <MaterialCommunityIcons name="reply" size={16} color="#fff" />
                  <Text style={styles.replyBtnText}>Reply</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>
      )}

      {/* Mobile summary modal */}
      {!isTablet && (
        <Modal
          visible={summaryOpen}
          animationType="slide"
          transparent
          onRequestClose={() => setSummaryOpen(false)}
        >
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => setSummaryOpen(false)}
          >
            <TouchableOpacity activeOpacity={1} style={styles.modalSheet}>
              <ScrollView showsVerticalScrollIndicator={false}>
                {summaryContent}
              </ScrollView>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      )}
    </KeyboardAvoidingView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F4F6F8",
  },

  // Header
  header: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    paddingHorizontal: 16,
    paddingVertical: 13,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  backBtn: {
    padding: 4,
    marginRight: 10,
  },
  headerTitle: {
    flex: 1,
    fontSize: 17,
    fontWeight: "700",
    color: "#111827",
  },
  infoBtn: {
    padding: 4,
  },

  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },

  // Body layout
  body: {
    flex: 1,
  },
  bodyTablet: {
    flexDirection: "row",
  },

  // Summary panel
  summaryColumn: {
    width: 280,
    backgroundColor: "#fff",
    borderRightWidth: 1,
    borderRightColor: "#E5E7EB",
  },
  summaryPanel: {
    padding: 20,
  },
  summaryTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  summaryTitle: {
    fontSize: 15,
    fontWeight: "700",
    color: "#111827",
  },
  verticalDivider: {
    width: 1,
    backgroundColor: "#E5E7EB",
  },

  // Field rows
  fieldRow: {
    marginBottom: 14,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: "700",
    color: "#2563EB",
    marginBottom: 2,
  },
  fieldValue: {
    fontSize: 14,
    color: "#111827",
    flexWrap: "wrap",
  },

  // Share button
  shareBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#2563EB",
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    marginTop: 8,
    alignSelf: "flex-start",
  },
  shareBtnText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#fff",
  },

  // Thread column
  threadColumn: {
    flex: 1,
    backgroundColor: "#fff",
  },
  threadScroll: {
    flex: 1,
    padding: 16,
  },
  emptyText: {
    color: "#9CA3AF",
    fontSize: 14,
    textAlign: "center",
    marginTop: 40,
  },

  // Message thread
  messageThread: {
    marginBottom: 8,
  },
  messageHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: 8,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#D1D5DB",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    fontSize: 18,
    fontWeight: "800",
    color: "#374151",
  },
  messageHeaderText: {
    flex: 1,
  },
  messageSender: {
    fontSize: 14,
    fontWeight: "700",
    color: "#111827",
  },
  messageTime: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 1,
  },
  messageBody: {
    fontSize: 14,
    color: "#374151",
    lineHeight: 22,
    marginLeft: 50,
  },
  attachmentsRow: {
    marginLeft: 50,
    marginTop: 10,
  },
  attachmentThumb: {
    width: 80,
    height: 80,
    borderRadius: 8,
    marginRight: 8,
    backgroundColor: "#F3F4F6",
  },
  threadDivider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginVertical: 16,
  },

  // Reply box
  replyBox: {
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    padding: 14,
    backgroundColor: "#fff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 6,
  },
  replyInput: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    padding: 12,
    fontSize: 14,
    color: "#111827",
    minHeight: 100,
    backgroundColor: "#FAFAFA",
  },
  attachPreviewRow: {
    marginTop: 10,
  },
  attachPreviewItem: {
    position: "relative",
    marginRight: 10,
  },
  attachPreviewThumb: {
    width: 64,
    height: 64,
    borderRadius: 8,
    backgroundColor: "#F3F4F6",
  },
  attachRemoveBtn: {
    position: "absolute",
    top: -6,
    right: -6,
    backgroundColor: "#fff",
    borderRadius: 999,
  },
  attachSpinner: {
    position: "absolute",
    bottom: 4,
    right: 4,
  },
  replyActions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 12,
  },
  attachBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#111827",
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
  },
  attachBtnText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600",
  },
  replyActionRight: {
    flexDirection: "row",
    gap: 10,
  },
  cancelBtn: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D1D5DB",
  },
  cancelBtnText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#374151",
  },
  submitBtn: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: "#2563EB",
  },
  submitBtnDisabled: {
    backgroundColor: "#93C5FD",
  },
  submitBtnText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#fff",
  },

  // Reply button row
  replyBtnRow: {
    padding: 14,
    alignItems: "flex-end",
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    backgroundColor: "#fff",
  },
  replyBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#2563EB",
    paddingVertical: 9,
    paddingHorizontal: 18,
    borderRadius: 8,
  },
  replyBtnText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "700",
  },

  // Mobile modal sheet
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.35)",
    justifyContent: "flex-end",
  },
  modalSheet: {
    backgroundColor: "#fff",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: "80%",
    paddingBottom: 24,
  },
});
